import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import i18next from 'i18next';
import type { LatLngTuple } from 'leaflet';
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Tooltip as MuiTooltip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { userSelector } from 'helper/security';
import { allCountries, allCountriesByCode } from 'languages';
import {
  APPROVE_MEETING_PACKAGE_CREATE,
  CANCEL_MEETING_PACKAGE_DISCOVERY,
  CLEAR_MEETING_PACKAGE_MATCHING_DRY_RUNS,
  DELETE_MEETING_PACKAGE_MATCHING_DRY_RUN,
  IGNORE_MEETING_PACKAGE_VENUE,
  MEETING_PACKAGE_HOTEL_CANDIDATES_QUERY,
  MEETING_PACKAGE_INACTIVE_COUNT_QUERY,
  MEETING_PACKAGE_IMPORT_ERRORS_QUERY,
  MEETING_PACKAGE_DRY_RUNS_QUERY,
  MEETING_PACKAGE_QUEUE_QUERY,
  MEETING_PACKAGE_QUEUE_COUNTS_QUERY,
  MEETING_PACKAGE_RUNS_QUERY,
  MEETING_PACKAGE_VENUE_QUERY,
  REEVALUATE_MEETING_PACKAGE_VENUE,
  RESET_MEETING_PACKAGE_CREATED_HOTEL,
  RETRY_MEETING_PACKAGE_VENUE,
  REVIEW_MEETING_PACKAGE_MATCH,
  RUN_MEETING_PACKAGE_DISCOVERY,
  RUN_MEETING_PACKAGE_MATCHING_DRY_RUN,
  UNLINK_MEETING_PACKAGE_MATCH,
} from './gql';

const SHOW_MATCHING_DRY_RUN = false;

type QueueVenue = {
  id: number;
  externalVenueId: string;
  venueName: string;
  resolution: string;
  status: string;
  hotelId?: number | null;
  payloadHash: string;
  matchScore?: number | null;
  runnerUpScore?: number | null;
  evidenceCoverage?: number | null;
  matchMethod?: string | null;
  matchExplanationJson?: string | null;
  lastSeenAt: string;
  retryCount: number;
  lastError?: string | null;
};

type ImportRunSummary = {
  id: number;
  countries: string[];
  startedAt: string;
  status: string;
  cancelRequestedAt?: string | null;
  totalVenueCount?: number | null;
  processedVenueCount: number;
  discoveredCount: number;
  apiVenueCount?: number | null;
  retryVenueCount?: number | null;
  prefilteredVenueCount?: number | null;
  excludedVenueCount?: number | null;
  updatedCount?: number | null;
  ignoredCount?: number | null;
  discoveredFailedCount?: number | null;
  accountingReconstructed?: boolean | null;
  linkedCount: number;
  createdCount: number;
  autoMatchEnabled?: boolean | null;
  autoCreateEnabled?: boolean | null;
  suggestedLinkedCount: number;
  suggestedCreatedCount: number;
  reviewCount: number;
  inactiveCount: number;
  failedCount: number;
  error?: string | null;
};

const importRunPercent = (run: ImportRunSummary): number | null => {
  if (run.totalVenueCount == null) return null;
  if (run.totalVenueCount === 0) return run.status === 'RUNNING' ? 0 : 100;
  return Math.min(100, Math.round((run.processedVenueCount / run.totalVenueCount) * 100));
};

type HotelCandidate = {
  hotelId: number;
  hotelName: string;
  businessAddress1?: string | null;
  businessZip?: string | null;
  businessCity?: string | null;
  businessCountry?: string | null;
  locationLatitude?: number | null;
  locationLongitude?: number | null;
  score: number;
  nameScore: number;
  evidenceCoverage: number;
  geoDistanceKm?: number;
  reasons: string[];
  conflicts: string[];
};

type MatchExplanation = {
  decision: string;
  reasons: string[];
  missingRequiredFields: string[];
  automationDisabledFor?: 'AUTO_MATCH' | 'AUTO_CREATE';
};

type DryRunScenarioSummary = {
  scenario: string;
  cases: number;
  passRate: number;
  autoMatches: number;
  reviews: number;
  autoCreates: number;
  correctTopCandidate: number;
  incorrectAutoMatches: number;
  scoreP10?: number;
  scoreP50?: number;
  scoreP90?: number;
  evidenceCoverageP50?: number;
  runnerUpGapP50?: number;
};

type DryRunThreshold = {
  threshold: number;
  minimumName: number;
  truePositives: number;
  falsePositives: number;
  falseNegatives: number;
  precision: number | null;
  recall: number | null;
};

type DryRunFailure = {
  id: string;
  scenario: string;
  expectation: string;
  venueName: string;
  expectedHotelId?: number;
  candidateHotelId?: number;
  candidateHotelName?: string;
  decision: string;
  score?: number;
  runnerUpScore?: number;
  evidenceCoverage?: number;
  conflicts: string[];
};

type DryRunReviewItem = {
  id: string;
  scenario: string;
  expectation: string;
  expectedHotelId?: number;
  venue: {
    name: string;
    businessAddress1?: string | null;
    businessZip?: string | null;
    businessCity?: string | null;
    businessCountry?: string | null;
    locationLatitude?: number | null;
    locationLongitude?: number | null;
  };
  decision: {
    decision: string;
    candidateHotelId?: number;
    score?: number;
    runnerUpScore?: number;
    evidenceCoverage?: number;
    confidence: string;
    reasons: string[];
    conflicts: string[];
    candidates: HotelCandidate[];
  };
};

type DryRunReport = {
  source?: 'MOCKED_DATABASE' | 'SANDBOX_API';
  generatedAt: string;
  databaseHotels: number;
  sampledHotels: number;
  generatedCases: number;
  overall: {
    passed: number | null;
    passRate: number | null;
    correctAutoMatches: number | null;
    incorrectAutoMatches: number | null;
    autoMatches?: number;
    reviews: number;
    incompletes?: number;
    autoCreates: number;
  };
  scenarios?: DryRunScenarioSummary[];
  thresholdCalibration?: DryRunThreshold[];
  items?: DryRunReviewItem[];
  reviewQueue?: {
    totalCount: number;
    items: DryRunReviewItem[];
  };
  failures?: DryRunFailure[];
  api?: {
    configuredVenues: number;
    requestedVenues: number;
    loadedVenues: number;
    failedVenues: number;
    language: string;
  };
  apiFailures?: Array<{ externalVenueId: string; venueName?: string | null; error: string }>;
  scoreDistribution?: { withCandidate: number; averageScore: number | null };
};

type DryRun = {
  id: number;
  source: 'MOCKED_DATABASE' | 'SANDBOX_API';
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  sampleSize: number;
  seed: number;
  databaseHotelCount: number;
  generatedCaseCount: number;
  passRate?: number | null;
  incorrectAutoMatchCount: number;
  reportJson?: string | null;
  error?: string | null;
};

type DryRunQueryData = {
  meetingPackageMatchingDryRunConfig: {
    enabled: boolean;
    defaultSampleSize: number;
    defaultSeed: number;
    sandboxEnabled: boolean;
    sandboxConfigured: boolean;
    defaultSandboxMaxVenues: number;
  };
  meetingPackageMatchingDryRuns: {
    totalCount: number;
    runs: DryRun[];
  };
};

const percent = (value?: number | null) => (value == null ? '–' : `${Math.round(value * 100)} %`);

const importValueLabel = (value?: string | null) => {
  if (!value) return '–';
  const key = `meeting-package-import-value-${value.toLowerCase().replaceAll('_', '-')}`;
  return i18next.exists(key) ? String(i18next.t(key)) : value;
};

const automaticResultLabel = (venue: QueueVenue): string | null => {
  if (venue.matchMethod === 'AUTO_CREATE' && venue.resolution === 'CREATED_FROM_SOURCE' && venue.hotelId)
    return i18next.t('meeting-package-auto-create-result-created');
  if (venue.matchMethod === 'AUTO_MATCH' && venue.resolution === 'LINKED_EXISTING' && venue.hotelId)
    return i18next.t('meeting-package-auto-match-result-linked');
  const disabledFor = parseJson<MatchExplanation>(venue.matchExplanationJson)?.automationDisabledFor;
  if (venue.status === 'REVIEW_REQUIRED' && !venue.hotelId && disabledFor === venue.matchMethod) {
    if (disabledFor === 'AUTO_CREATE') return i18next.t('meeting-package-auto-create-result-disabled');
    if (disabledFor === 'AUTO_MATCH') return i18next.t('meeting-package-auto-match-result-disabled');
  }
  return venue.matchMethod === 'AUTO_CREATE' ? importValueLabel(venue.status) : null;
};

const coordinatePair = (latitude?: number | null, longitude?: number | null) =>
  latitude == null || longitude == null || !Number.isFinite(latitude) || !Number.isFinite(longitude)
    ? '–'
    : `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

const finiteNumber = (value: unknown) => {
  const number = typeof value === 'number' ? value : typeof value === 'string' ? Number.parseFloat(value) : undefined;
  return number != null && Number.isFinite(number) ? number : undefined;
};

const MapViewport = ({ venue, candidate }: { venue: LatLngTuple; candidate?: LatLngTuple }) => {
  const map = useMap();

  useEffect(() => {
    map.invalidateSize();
    if (candidate) {
      map.fitBounds([venue, candidate], { padding: [48, 48], maxZoom: 18 });
    } else {
      map.setView(venue, 17);
    }
  }, [candidate, map, venue]);

  return null;
};

const VenueComparisonMap = ({
  venueLatitude,
  venueLongitude,
  candidate,
}: {
  venueLatitude: number;
  venueLongitude: number;
  candidate?: HotelCandidate;
}) => {
  const venuePosition: LatLngTuple = [venueLatitude, venueLongitude];
  const candidatePosition: LatLngTuple | undefined =
    candidate?.locationLatitude != null &&
    candidate.locationLongitude != null &&
    Number.isFinite(candidate.locationLatitude) &&
    Number.isFinite(candidate.locationLongitude)
      ? [candidate.locationLatitude, candidate.locationLongitude]
      : undefined;

  return (
    <MapContainer center={venuePosition} zoom={17} scrollWheelZoom={false} style={{ width: '100%', height: 300 }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapViewport venue={venuePosition} candidate={candidatePosition} />
      <CircleMarker center={venuePosition} radius={9} pathOptions={{ color: '#fff', weight: 3, fillColor: '#b0005e', fillOpacity: 1 }}>
        <Tooltip permanent direction="top" offset={[0, -10]}>
          {i18next.t('meeting-package-map-source-marker')}
        </Tooltip>
      </CircleMarker>
      {candidatePosition && (
        <CircleMarker center={candidatePosition} radius={9} pathOptions={{ color: '#fff', weight: 3, fillColor: '#1976d2', fillOpacity: 1 }}>
          <Tooltip permanent direction="top" offset={[0, -10]}>
            {i18next.t('meeting-package-map-candidate-marker', { name: candidate?.hotelName })}
          </Tooltip>
        </CircleMarker>
      )}
    </MapContainer>
  );
};

const statusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (status === 'READY') return 'success';
  if (status === 'INCOMPLETE' || status === 'REVIEW_REQUIRED' || status === 'IMPORTED_PENDING_PROVISIONING') return 'warning';
  if (status.startsWith('FAILED')) return 'error';
  if (status === 'INACTIVE' || status === 'CANCELLED') return 'default';
  return 'info';
};

const parseJson = <T,>(value?: string | null, fallback?: T): T | undefined => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const parseJsonArray = <T,>(value?: string | null): T[] => {
  const parsed = parseJson<unknown>(value, []);
  return Array.isArray(parsed) ? (parsed as T[]) : [];
};

const VenueReviewDialog = ({ externalVenueId, onClose }: { externalVenueId: string; onClose: () => void }) => {
  const [hotelSearch, setHotelSearch] = useState('');
  const [note, setNote] = useState('');
  const [countryOverride, setCountryOverride] = useState('');
  const [actionError, setActionError] = useState<string>();
  const [unlinkConfirmationOpen, setUnlinkConfirmationOpen] = useState(false);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const [selectedCandidateHotelId, setSelectedCandidateHotelId] = useState<number>();
  const [detailTab, setDetailTab] = useState(0);
  const { data, loading, refetch } = useQuery(MEETING_PACKAGE_VENUE_QUERY, { variables: { externalVenueId } });
  const { data: hotelData } = useQuery(MEETING_PACKAGE_HOTEL_CANDIDATES_QUERY, {
    variables: { search: hotelSearch || null, skip: 0, take: 20 },
    skip: hotelSearch.trim().length < 2,
  });
  const refetchQueries = [
    MEETING_PACKAGE_QUEUE_QUERY,
    MEETING_PACKAGE_QUEUE_COUNTS_QUERY,
    MEETING_PACKAGE_INACTIVE_COUNT_QUERY,
    MEETING_PACKAGE_RUNS_QUERY,
    MEETING_PACKAGE_VENUE_QUERY,
  ];
  const [matchHotel, matchState] = useMutation(REVIEW_MEETING_PACKAGE_MATCH, { refetchQueries });
  const [createHotel, createState] = useMutation(APPROVE_MEETING_PACKAGE_CREATE, { refetchQueries });
  const [ignoreVenue, ignoreState] = useMutation(IGNORE_MEETING_PACKAGE_VENUE, { refetchQueries });
  const [reevaluate, reevaluateState] = useMutation(REEVALUATE_MEETING_PACKAGE_VENUE, { refetchQueries });
  const [retry, retryState] = useMutation(RETRY_MEETING_PACKAGE_VENUE, { refetchQueries });
  const [unlinkMatch, unlinkState] = useMutation(UNLINK_MEETING_PACKAGE_MATCH, { refetchQueries });
  const [resetCreatedHotel, resetState] = useMutation(RESET_MEETING_PACKAGE_CREATED_HOTEL, { refetchQueries });
  const venue = data?.meetingPackageVenue;
  const candidates = useMemo(() => parseJsonArray<HotelCandidate>(venue?.topCandidatesJson), [venue?.topCandidatesJson]);
  const matchExplanation = useMemo(() => parseJson<MatchExplanation>(venue?.matchExplanationJson), [venue?.matchExplanationJson]);
  const payload = useMemo(() => parseJson<Record<string, unknown>>(venue?.payloadJson, {}) || {}, [venue?.payloadJson]);
  const payloadAddress = (payload.address || {}) as Record<string, string | undefined>;
  const latitude = finiteNumber(payload.latitude);
  const longitude = finiteNumber(payload.longitude);
  const selectedCandidate = candidates.find(candidate => candidate.hotelId === selectedCandidateHotelId) || candidates[0];
  const selectedCandidateHasCoordinates =
    selectedCandidate?.locationLatitude != null &&
    selectedCandidate.locationLongitude != null &&
    Number.isFinite(selectedCandidate.locationLatitude) &&
    Number.isFinite(selectedCandidate.locationLongitude);
  const busy =
    matchState.loading ||
    createState.loading ||
    ignoreState.loading ||
    reevaluateState.loading ||
    retryState.loading ||
    unlinkState.loading ||
    resetState.loading;

  const action = async (callback: () => Promise<unknown>) => {
    setActionError(undefined);
    try {
      await callback();
      await refetch();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : String(error));
    }
  };

  const link = (hotelId: number) => action(() => matchHotel({ variables: { externalVenueId, hotelId, expectedPayloadHash: venue.payloadHash } }));

  return (
    <>
      <Dialog open maxWidth="lg" fullWidth onClose={busy ? undefined : onClose}>
        <DialogTitle>{venue?.venueName || externalVenueId}</DialogTitle>
        <DialogContent>
          {loading && <CircularProgress />}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          {venue && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={importValueLabel(['AUTO_CREATE', 'AUTO_MATCH'].includes(venue.matchMethod || '') ? venue.matchMethod : venue.status)}
                  color={statusColor(venue.status)}
                />
                {automaticResultLabel(venue) && <Chip label={automaticResultLabel(venue)} variant="outlined" />}
                <Chip label={importValueLabel(venue.resolution)} variant="outlined" />
                <Chip label={i18next.t('meeting-package-score-value', { value: percent(venue.matchScore) })} variant="outlined" />
                <Chip label={i18next.t('meeting-package-evidence-value', { value: percent(venue.evidenceCoverage) })} variant="outlined" />
              </Stack>
              {matchExplanation?.reasons.includes('MISSING_REQUIRED_FIELDS') && (
                <Alert severity="warning">
                  {i18next.t('meeting-package-missing-required-fields', {
                    fields: matchExplanation.missingRequiredFields.join(', '),
                    defaultValue: 'Missing required fields: {{fields}}',
                  })}
                </Alert>
              )}
              {venue.lastError && <Alert severity="error">{venue.lastError}</Alert>}
              <Tabs value={detailTab} onChange={(_event, value: number) => setDetailTab(value)}>
                <Tab label={i18next.t('meeting-package-comparison')} />
                <Tab label={i18next.t('meeting-package-payload')} />
                <Tab label={i18next.t('meeting-package-process-log-count', { count: venue.events?.length || 0 })} />
              </Tabs>
              {detailTab === 0 && (
                <>
                  <Box>
                    <Typography variant="h6">{i18next.t('meeting-package-data')}</Typography>
                    <Table size="small" sx={{ mb: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-field')}</TableCell>
                          <TableCell>{i18next.t('meeting-package-source-name')}</TableCell>
                          <TableCell>
                            {i18next.t('meeting-package-selected-candidate')}
                            {selectedCandidate && ` · ${i18next.t('meeting-package-score-value', { value: percent(selectedCandidate.score) })}`}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-name')}</TableCell>
                          <TableCell>{String(payload.name || '')}</TableCell>
                          <TableCell>{selectedCandidate?.hotelName || '–'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-address')}</TableCell>
                          <TableCell>{payloadAddress.street || '–'}</TableCell>
                          <TableCell>{selectedCandidate?.businessAddress1 || '–'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-postcode-city')}</TableCell>
                          <TableCell>
                            {payloadAddress.postal_code || '–'} {payloadAddress.city || ''}
                          </TableCell>
                          <TableCell>
                            {selectedCandidate ? `${selectedCandidate.businessZip || '–'} ${selectedCandidate.businessCity || ''}` : '–'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-country')}</TableCell>
                          <TableCell>{payloadAddress.country || '–'}</TableCell>
                          <TableCell>{selectedCandidate?.businessCountry || '–'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-coordinates')}</TableCell>
                          <TableCell>{coordinatePair(latitude, longitude)}</TableCell>
                          <TableCell>{coordinatePair(selectedCandidate?.locationLatitude, selectedCandidate?.locationLongitude)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-geo-distance')}</TableCell>
                          <TableCell colSpan={2}>
                            {selectedCandidate?.geoDistanceKm == null ? '–' : `${selectedCandidate.geoDistanceKm.toFixed(2)} km`}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    {latitude != null && longitude != null && (
                      <Box sx={{ mb: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                          <Chip size="small" label={i18next.t('meeting-package-map-source-venue')} color="primary" variant="outlined" />
                          {selectedCandidateHasCoordinates && (
                            <Chip size="small" label={`B: ${selectedCandidate.hotelName}`} color="secondary" variant="outlined" />
                          )}
                        </Stack>
                        <Box sx={{ overflow: 'hidden', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <VenueComparisonMap
                            venueLatitude={latitude}
                            venueLongitude={longitude}
                            candidate={selectedCandidateHasCoordinates ? selectedCandidate : undefined}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {venue.hotel && (
                    <Alert
                      severity="success"
                      action={
                        venue.resolution === 'LINKED_EXISTING' ? (
                          <Button color="inherit" disabled={busy} onClick={() => setUnlinkConfirmationOpen(true)}>
                            {i18next.t('meeting-package-unlink')}
                          </Button>
                        ) : venue.resolution === 'CREATED_FROM_SOURCE' ? (
                          <Button color="inherit" disabled={busy} onClick={() => setResetConfirmationOpen(true)}>
                            {i18next.t('meeting-package-reset-import')}
                          </Button>
                        ) : undefined
                      }
                    >
                      <Stack spacing={0.5} alignItems="flex-start">
                        <Typography variant="body2">
                          {i18next.t('meeting-package-linked-with', { name: venue.hotel.name, refCode: venue.hotel.refCode })}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button component={Link} to={`/settings/hotels/${venue.hotel.id}`} color="inherit" size="small" variant="outlined">
                            {i18next.t('meeting-package-open-hotel')}
                          </Button>
                          <Button component={Link} to={`/settings/spaces/${venue.hotel.space.id}`} color="inherit" size="small" variant="outlined">
                            {i18next.t('meeting-package-administration-area', { name: venue.hotel.space.name })}
                          </Button>
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {i18next.t('meeting-package-suggested-hotels')}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18next.t('meeting-package-hotel')}</TableCell>
                          <TableCell>{i18next.t('meeting-package-address')}</TableCell>
                          <TableCell>{i18next.t('meeting-package-score')}</TableCell>
                          <TableCell>{i18next.t('meeting-package-reasons')}</TableCell>
                          <TableCell>{i18next.t('meeting-package-conflicts')}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {candidates.map(candidate => (
                          <TableRow key={candidate.hotelId} selected={candidate.hotelId === selectedCandidate?.hotelId}>
                            <TableCell>
                              {candidate.hotelName} (#{candidate.hotelId})
                            </TableCell>
                            <TableCell>
                              {candidate.businessAddress1 || '–'}, {candidate.businessZip || '–'} {candidate.businessCity || ''}
                            </TableCell>
                            <TableCell>{percent(candidate.score)}</TableCell>
                            <TableCell>{candidate.reasons.join(', ') || '–'}</TableCell>
                            <TableCell>{candidate.conflicts.join(', ') || '–'}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant={candidate.hotelId === selectedCandidate?.hotelId ? 'contained' : 'outlined'}
                                  disabled={busy}
                                  onClick={() => setSelectedCandidateHotelId(candidate.hotelId)}
                                >
                                  {i18next.t('meeting-package-compare')}
                                </Button>
                                <Button size="small" disabled={busy || venue.resolution !== 'UNRESOLVED'} onClick={() => link(candidate.hotelId)}>
                                  {i18next.t('meeting-package-link')}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  {venue.resolution === 'UNRESOLVED' && (
                    <Box>
                      <Typography variant="h6">{i18next.t('meeting-package-select-other-hotel')}</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={hotelSearch}
                        onChange={event => setHotelSearch(event.target.value)}
                        placeholder={String(i18next.t('meeting-package-hotel-search-placeholder'))}
                      />
                      {(hotelData?.meetingPackageHotelCandidates.hotels || []).map(
                        (hotel: { id: number; name: string; refCode: string; businessCity: string }) => (
                          <Stack
                            key={hotel.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ py: 1, borderBottom: '1px solid #eee' }}
                          >
                            <span>
                              {hotel.name} · {hotel.refCode} · {hotel.businessCity}
                            </span>
                            <Button disabled={busy} onClick={() => link(hotel.id)}>
                              {i18next.t('meeting-package-select')}
                            </Button>
                          </Stack>
                        ),
                      )}
                    </Box>
                  )}
                  <Divider />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      size="small"
                      label={i18next.t('meeting-package-country-iso-optional')}
                      value={countryOverride}
                      onChange={event => setCountryOverride(event.target.value.toUpperCase())}
                      inputProps={{ maxLength: 2 }}
                    />
                    <Button
                      variant="contained"
                      disabled={busy || venue.resolution !== 'UNRESOLVED' || venue.status === 'INCOMPLETE'}
                      onClick={() =>
                        action(() =>
                          createHotel({
                            variables: { externalVenueId, expectedPayloadHash: venue.payloadHash, countryOverride: countryOverride || null },
                          }),
                        )
                      }
                    >
                      {i18next.t('meeting-package-import-as-new-hotel')}
                    </Button>
                    <Button disabled={busy} startIcon={<RefreshIcon />} onClick={() => action(() => reevaluate({ variables: { externalVenueId } }))}>
                      {i18next.t('meeting-package-reevaluate')}
                    </Button>
                    {(venue.status === 'FAILED_RETRYABLE' || venue.status === 'IMPORTED_PENDING_PROVISIONING') && (
                      <Button disabled={busy} color="warning" onClick={() => action(() => retry({ variables: { externalVenueId } }))}>
                        {i18next.t('meeting-package-retry')}
                      </Button>
                    )}
                  </Stack>
                  {venue.resolution === 'UNRESOLVED' && (
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth
                        size="small"
                        label={i18next.t('meeting-package-note')}
                        value={note}
                        onChange={event => setNote(event.target.value)}
                      />
                      <Button
                        color="inherit"
                        disabled={busy}
                        onClick={() => action(() => ignoreVenue({ variables: { externalVenueId, note: note || null } }))}
                      >
                        {i18next.t('meeting-package-ignore')}
                      </Button>
                    </Stack>
                  )}
                </>
              )}
              {detailTab === 1 && (
                <Box
                  component="pre"
                  sx={{ m: 0, whiteSpace: 'pre-wrap', maxHeight: '60vh', overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}
                >
                  {JSON.stringify(payload, null, 2)}
                </Box>
              )}
              {detailTab === 2 && (
                <Stack spacing={1}>
                  {(venue.events || []).length === 0 && (
                    <Typography color="text.secondary">{i18next.t('meeting-package-no-process-log-entries')}</Typography>
                  )}
                  {(venue.events || []).map((event: { id: number; type: string; createdAt: string; detailsJson?: string }) => (
                    <Box key={event.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <strong>{new Date(event.createdAt).toLocaleString(i18next.language)}</strong> · {event.type}
                      {event.detailsJson && (
                        <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                          {event.detailsJson}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            {i18next.t('meeting-package-close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={unlinkConfirmationOpen} onClose={busy ? undefined : () => setUnlinkConfirmationOpen(false)}>
        <DialogTitle>{i18next.t('meeting-package-unlink-confirm-title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {i18next.t('meeting-package-unlink-confirm-text', { name: venue?.hotel?.name || i18next.t('meeting-package-this-hotel') })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setUnlinkConfirmationOpen(false)}>
            {i18next.t('meeting-package-cancel')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={() =>
              action(async () => {
                await unlinkMatch({ variables: { externalVenueId } });
                setUnlinkConfirmationOpen(false);
              })
            }
          >
            {i18next.t('meeting-package-unlink')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={resetConfirmationOpen} onClose={busy ? undefined : () => setResetConfirmationOpen(false)}>
        <DialogTitle>{i18next.t('meeting-package-reset-confirm-title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {i18next.t('meeting-package-reset-warning')}
          </Alert>
          <Typography>{i18next.t('meeting-package-reset-confirm-text')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setResetConfirmationOpen(false)}>
            {i18next.t('meeting-package-cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() =>
              action(async () => {
                await resetCreatedHotel({ variables: { externalVenueId } });
                setResetConfirmationOpen(false);
              })
            }
          >
            {i18next.t('meeting-package-delete-hotel-and-area')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DryRunReviewDialog = ({ item, onClose }: { item: DryRunReviewItem; onClose: () => void }) => {
  const best = item.decision.candidates[0];
  return (
    <Dialog open maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle>
        {item.venue.name} · {item.scenario}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {i18next.t(item.scenario === 'SANDBOX_API' ? 'meeting-package-dry-run-real-venue-info' : 'meeting-package-dry-run-simulated-case-info')}
        </Alert>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label={i18next.t('meeting-package-expectation-value', { value: importValueLabel(item.expectation) })} />
          <Chip label={i18next.t('meeting-package-decision-value', { value: importValueLabel(item.decision.decision) })} color="warning" />
          <Chip label={i18next.t('meeting-package-score-value', { value: percent(item.decision.score) })} />
          <Chip label={i18next.t('meeting-package-evidence-value', { value: percent(item.decision.evidenceCoverage) })} />
          <Chip label={i18next.t('meeting-package-runner-up-value', { value: percent(item.decision.runnerUpScore) })} />
        </Stack>
        <Typography variant="h6">{i18next.t('meeting-package-field-comparison')}</Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-field')}</TableCell>
              <TableCell>{i18next.t(item.scenario === 'SANDBOX_API' ? 'meeting-package-sandbox-venue' : 'meeting-package-mock-venue')}</TableCell>
              <TableCell>{i18next.t('meeting-package-best-candidate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-name')}</TableCell>
              <TableCell>{item.venue.name}</TableCell>
              <TableCell>{best?.hotelName || '–'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-address')}</TableCell>
              <TableCell>{item.venue.businessAddress1 || '–'}</TableCell>
              <TableCell>{best?.businessAddress1 || '–'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-postcode-city')}</TableCell>
              <TableCell>
                {item.venue.businessZip || '–'} {item.venue.businessCity || ''}
              </TableCell>
              <TableCell>
                {best?.businessZip || '–'} {best?.businessCity || ''}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-country')}</TableCell>
              <TableCell>{item.venue.businessCountry || '–'}</TableCell>
              <TableCell>{best?.businessCountry || '–'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-coordinates')}</TableCell>
              <TableCell>{coordinatePair(item.venue.locationLatitude, item.venue.locationLongitude)}</TableCell>
              <TableCell>{coordinatePair(best?.locationLatitude, best?.locationLongitude)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-geo-distance')}</TableCell>
              <TableCell colSpan={2}>{best?.geoDistanceKm == null ? '–' : `${best.geoDistanceKm.toFixed(2)} km`}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Typography variant="h6">{i18next.t('meeting-package-top-candidates')}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-hotel')}</TableCell>
              <TableCell>{i18next.t('meeting-package-address')}</TableCell>
              <TableCell>{i18next.t('meeting-package-score')}</TableCell>
              <TableCell>{i18next.t('meeting-package-name')}</TableCell>
              <TableCell>{i18next.t('meeting-package-evidence')}</TableCell>
              <TableCell>{i18next.t('meeting-package-reasons')}</TableCell>
              <TableCell>{i18next.t('meeting-package-conflicts')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item.decision.candidates.map(candidate => (
              <TableRow key={candidate.hotelId}>
                <TableCell>
                  {candidate.hotelName} (#{candidate.hotelId})
                </TableCell>
                <TableCell>
                  {candidate.businessAddress1 || '–'}, {candidate.businessZip || '–'} {candidate.businessCity || ''}
                </TableCell>
                <TableCell>{percent(candidate.score)}</TableCell>
                <TableCell>{percent(candidate.nameScore)}</TableCell>
                <TableCell>{percent(candidate.evidenceCoverage)}</TableCell>
                <TableCell>{candidate.reasons.join(', ') || '–'}</TableCell>
                <TableCell>{candidate.conflicts.join(', ') || '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18next.t('meeting-package-close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const MatchingDryRunPanel = ({ query }: { query: ReturnType<typeof useQuery> }) => {
  const queryData = query.data as DryRunQueryData | undefined;
  const config = queryData?.meetingPackageMatchingDryRunConfig;
  const allRuns = queryData?.meetingPackageMatchingDryRuns.runs || [];
  const [sampleSize, setSampleSize] = useState(200);
  const [seed, setSeed] = useState(20260715);
  const [source, setSource] = useState<'MOCKED_DATABASE' | 'SANDBOX_API'>('MOCKED_DATABASE');
  const [selectedRunId, setSelectedRunId] = useState<number>();
  const [selectedReviewItem, setSelectedReviewItem] = useState<DryRunReviewItem>();
  const [reportTab, setReportTab] = useState(0);
  const [runDryRun, runState] = useMutation(RUN_MEETING_PACKAGE_MATCHING_DRY_RUN, {
    refetchQueries: [MEETING_PACKAGE_DRY_RUNS_QUERY],
  });
  const [deleteDryRun, deleteState] = useMutation(DELETE_MEETING_PACKAGE_MATCHING_DRY_RUN, {
    refetchQueries: [MEETING_PACKAGE_DRY_RUNS_QUERY],
  });
  const [clearDryRuns, clearState] = useMutation(CLEAR_MEETING_PACKAGE_MATCHING_DRY_RUNS, {
    refetchQueries: [MEETING_PACKAGE_DRY_RUNS_QUERY],
  });

  useEffect(() => {
    if (config) {
      if (!config.enabled && config.sandboxEnabled && source !== 'SANDBOX_API') {
        setSource('SANDBOX_API');
        return;
      }
      setSampleSize(source === 'SANDBOX_API' ? config.defaultSandboxMaxVenues : config.defaultSampleSize);
      setSeed(config.defaultSeed);
    }
  }, [config, source]);

  useEffect(() => {
    setSelectedRunId(undefined);
    setSelectedReviewItem(undefined);
    setReportTab(0);
  }, [source]);

  const runs = allRuns.filter(run => run.source === source);

  const selectedRun = runs.find(run => run.id === selectedRunId) || runs[0];
  const report = useMemo(() => parseJson<DryRunReport>(selectedRun?.reportJson), [selectedRun?.reportJson]);
  const isSandboxReport = report?.source === 'SANDBOX_API' || selectedRun?.source === 'SANDBOX_API';
  const reportFailures = report?.failures || [];

  const start = async () => {
    const result = await runDryRun({ variables: { source, sampleSize, seed: source === 'MOCKED_DATABASE' ? seed : null } });
    const id = result.data?.runMeetingPackageMatchingDryRun.id;
    if (id) setSelectedRunId(id);
    setReportTab(0);
    setSelectedReviewItem(undefined);
    await query.refetch();
  };

  const removeRun = async (id: number) => {
    if (!window.confirm(String(i18next.t('meeting-package-delete-report-confirm')))) return;
    await deleteDryRun({ variables: { id } });
    if (selectedRunId === id) setSelectedRunId(undefined);
    setReportTab(0);
    setSelectedReviewItem(undefined);
    await query.refetch();
  };

  const clearRuns = async () => {
    const modeLabel = i18next.t(source === 'SANDBOX_API' ? 'meeting-package-sandbox-api' : 'meeting-package-mock-scenario');
    if (!window.confirm(String(i18next.t('meeting-package-delete-all-reports-confirm', { mode: modeLabel })))) return;
    await clearDryRuns({ variables: { source } });
    setSelectedRunId(undefined);
    setReportTab(0);
    setSelectedReviewItem(undefined);
    await query.refetch();
  };

  return (
    <Box mt={2}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {i18next.t('meeting-package-dry-run-info')}
      </Alert>
      <Tabs value={source} onChange={(_event, value) => setSource(value)} sx={{ mb: 2 }}>
        <Tab value="MOCKED_DATABASE" label={i18next.t('meeting-package-mock-scenarios')} disabled={!config?.enabled} />
        <Tab value="SANDBOX_API" label={i18next.t('meeting-package-sandbox-api')} disabled={!config?.sandboxEnabled || !config?.sandboxConfigured} />
      </Tabs>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} mb={2}>
        <TextField
          size="small"
          type="number"
          label={i18next.t(source === 'SANDBOX_API' ? 'meeting-package-max-api-venues' : 'meeting-package-db-hotel-sample')}
          value={sampleSize}
          onChange={event => setSampleSize(Math.max(1, Math.min(1000, Number(event.target.value))))}
          inputProps={{ min: 1, max: 1000 }}
        />
        {source === 'MOCKED_DATABASE' && (
          <TextField
            size="small"
            type="number"
            label={i18next.t('meeting-package-seed')}
            value={seed}
            onChange={event => setSeed(Math.max(1, Number(event.target.value)))}
          />
        )}
        <Button
          variant="contained"
          startIcon={runState.loading ? <CircularProgress size={18} /> : <PlayArrowIcon />}
          disabled={runState.loading}
          onClick={start}
        >
          {i18next.t(source === 'SANDBOX_API' ? 'meeting-package-start-sandbox-dry-run' : 'meeting-package-start-matching-dry-run')}
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={() => query.refetch()}>
          {i18next.t('meeting-package-refresh')}
        </Button>
        <Button color="error" disabled={clearState.loading || runs.every(run => run.status === 'RUNNING')} onClick={clearRuns}>
          {i18next.t('meeting-package-delete-completed-reports')}
        </Button>
      </Stack>
      {config?.sandboxEnabled && !config.sandboxConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {i18next.t('meeting-package-sandbox-config-missing')}
        </Alert>
      )}
      {(runState.error || deleteState.error || clearState.error) && (
        <Alert severity="error">{runState.error?.message || deleteState.error?.message || clearState.error?.message}</Alert>
      )}
      {query.error && <Alert severity="error">{query.error.message}</Alert>}
      {query.loading && <CircularProgress />}

      <Typography variant="h6" gutterBottom>
        {i18next.t(source === 'SANDBOX_API' ? 'meeting-package-sandbox-history' : 'meeting-package-mock-history')}
      </Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>{i18next.t('meeting-package-start')}</TableCell>
            <TableCell>{i18next.t('meeting-package-source')}</TableCell>
            <TableCell>{i18next.t('meeting-package-status')}</TableCell>
            <TableCell>{i18next.t('meeting-package-limit-seed')}</TableCell>
            <TableCell>{i18next.t('meeting-package-cases')}</TableCell>
            <TableCell>{i18next.t('meeting-package-pass-rate')}</TableCell>
            <TableCell>{i18next.t('meeting-package-incorrect-auto-matches')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map(run => (
            <TableRow key={run.id} selected={selectedRun?.id === run.id}>
              <TableCell>{new Date(run.startedAt).toLocaleString(i18next.language)}</TableCell>
              <TableCell>{i18next.t(run.source === 'SANDBOX_API' ? 'meeting-package-sandbox-api' : 'meeting-package-mock-scenarios')}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={importValueLabel(run.status)}
                  color={run.status === 'SUCCESS' ? 'success' : run.status === 'FAILED' ? 'error' : 'info'}
                />
              </TableCell>
              <TableCell>
                {run.sampleSize} / {run.source === 'SANDBOX_API' ? '–' : run.seed}
              </TableCell>
              <TableCell>{run.generatedCaseCount}</TableCell>
              <TableCell>{run.source === 'SANDBOX_API' ? i18next.t('meeting-package-no-ground-truth') : percent(run.passRate)}</TableCell>
              <TableCell>{run.source === 'SANDBOX_API' ? '–' : run.incorrectAutoMatchCount}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => {
                      setSelectedRunId(run.id);
                      setSelectedReviewItem(undefined);
                      setReportTab(0);
                    }}
                  >
                    {i18next.t('meeting-package-show')}
                  </Button>
                  <Button color="error" disabled={run.status === 'RUNNING' || deleteState.loading} onClick={() => removeRun(run.id)}>
                    {i18next.t('meeting-package-delete')}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selectedRun?.error && <Alert severity="error">{selectedRun.error}</Alert>}
      {report && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {i18next.t('meeting-package-result-from', {
                date: new Date(report.generatedAt).toLocaleString(i18next.language),
                interpolation: { escapeValue: false },
              })}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={i18next.t('meeting-package-db-hotels-count', { count: report.databaseHotels })} />
              <Chip
                label={i18next.t(isSandboxReport ? 'meeting-package-api-venues-loaded-count' : 'meeting-package-in-sample-count', {
                  count: report.sampledHotels,
                })}
              />
              <Chip
                label={i18next.t(isSandboxReport ? 'meeting-package-real-venues-count' : 'meeting-package-mock-cases-count', {
                  count: report.generatedCases,
                })}
              />
              {isSandboxReport ? (
                <>
                  <Chip
                    label={i18next.t('meeting-package-auto-match-candidates-count', { count: report.overall.autoMatches || 0 })}
                    color="success"
                  />
                  <Chip label={i18next.t('meeting-package-reviews-count', { count: report.overall.reviews })} color="warning" />
                  <Chip label={i18next.t('meeting-package-auto-create-candidates-count', { count: report.overall.autoCreates })} />
                  <Chip
                    label={i18next.t('meeting-package-api-errors-count', { count: report.api?.failedVenues || 0 })}
                    color={report.api?.failedVenues ? 'error' : 'default'}
                  />
                </>
              ) : (
                <>
                  <Chip label={i18next.t('meeting-package-pass-rate-value', { value: percent(report.overall.passRate) })} />
                  <Chip
                    label={i18next.t('meeting-package-correct-auto-matches-count', { count: report.overall.correctAutoMatches ?? 0 })}
                    color="success"
                  />
                  <Chip
                    label={i18next.t('meeting-package-incorrect-auto-matches-count', { count: report.overall.incorrectAutoMatches ?? 0 })}
                    color={report.overall.incorrectAutoMatches === 0 ? 'success' : 'error'}
                  />
                </>
              )}
            </Stack>
            {isSandboxReport && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {i18next.t('meeting-package-sandbox-no-ground-truth-info')}
              </Alert>
            )}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={reportTab} onChange={(_event, value) => setReportTab(value)} variant="scrollable" scrollButtons="auto">
              <Tab
                label={
                  isSandboxReport
                    ? i18next.t('meeting-package-decisions-count', { count: report.items?.length || 0 })
                    : i18next.t('meeting-package-thresholds')
                }
              />
              {!isSandboxReport && <Tab label={i18next.t('meeting-package-scenarios')} />}
              <Tab label={i18next.t('meeting-package-review-queue-count', { count: report.reviewQueue?.totalCount ?? report.overall.reviews })} />
              <Tab
                label={
                  isSandboxReport
                    ? i18next.t('meeting-package-api-errors-count', { count: report.apiFailures?.length || 0 })
                    : i18next.t('meeting-package-deviations-count', { count: reportFailures.length })
                }
              />
            </Tabs>
          </Box>

          {!isSandboxReport && (
            <Box sx={{ display: reportTab === 0 ? 'block' : 'none' }}>
              <Typography variant="h6">{i18next.t('meeting-package-threshold-calibration')}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18next.t('meeting-package-total-score')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-name-score')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-true-positives')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-false-positives')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-false-negatives')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-precision')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-recall')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report.thresholdCalibration || []).map(item => (
                    <TableRow key={`${item.threshold}:${item.minimumName}`} selected={item.threshold === 0.92 && item.minimumName === 0.85}>
                      <TableCell>{percent(item.threshold)}</TableCell>
                      <TableCell>{percent(item.minimumName)}</TableCell>
                      <TableCell>{item.truePositives}</TableCell>
                      <TableCell sx={{ color: item.falsePositives > 0 ? 'error.main' : undefined }}>{item.falsePositives}</TableCell>
                      <TableCell>{item.falseNegatives}</TableCell>
                      <TableCell>{percent(item.precision)}</TableCell>
                      <TableCell>{percent(item.recall)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {!isSandboxReport && (
            <Box sx={{ display: reportTab === 1 ? 'block' : 'none' }}>
              <Typography variant="h6">{i18next.t('meeting-package-scenarios')}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18next.t('meeting-package-scenario')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-cases')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-pass-rate')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-top-candidate-correct')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-auto-match')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-review')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-auto-create')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-score-percentiles')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report.scenarios || []).map(item => (
                    <TableRow key={item.scenario}>
                      <TableCell>{item.scenario}</TableCell>
                      <TableCell>{item.cases}</TableCell>
                      <TableCell>{percent(item.passRate)}</TableCell>
                      <TableCell>{item.correctTopCandidate}</TableCell>
                      <TableCell>{item.autoMatches}</TableCell>
                      <TableCell>{item.reviews}</TableCell>
                      <TableCell>{item.autoCreates}</TableCell>
                      <TableCell>
                        {percent(item.scoreP10)} / {percent(item.scoreP50)} / {percent(item.scoreP90)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {isSandboxReport && (
            <Box sx={{ display: reportTab === 0 ? 'block' : 'none' }}>
              <Typography variant="h6">{i18next.t('meeting-package-sandbox-match-decisions')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {i18next.t('meeting-package-sandbox-match-decisions-info')}
              </Typography>
              <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18next.t('meeting-package-api-venue')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-decision')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-best-candidate')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-score')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-evidence')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-conflicts')}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.items || []).map(item => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          {item.venue.name}
                          <Typography variant="caption" display="block">
                            ID {item.id} · {item.venue.businessAddress1 || '–'}, {item.venue.businessZip || '–'} {item.venue.businessCity || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>{importValueLabel(item.decision.decision)}</TableCell>
                        <TableCell>{item.decision.candidates[0]?.hotelName || '–'}</TableCell>
                        <TableCell>{percent(item.decision.score)}</TableCell>
                        <TableCell>{percent(item.decision.evidenceCoverage)}</TableCell>
                        <TableCell>{item.decision.conflicts.join(', ') || '–'}</TableCell>
                        <TableCell>
                          <Button onClick={() => setSelectedReviewItem(item)}>{i18next.t('meeting-package-details')}</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          <Box sx={{ display: reportTab === (isSandboxReport ? 1 : 2) ? 'block' : 'none' }}>
            <Typography variant="h6">
              {i18next.t(isSandboxReport ? 'meeting-package-sandbox-review-queue-summary' : 'meeting-package-simulated-review-queue-summary', {
                shown: report.reviewQueue?.items.length || 0,
                total: report.reviewQueue?.totalCount ?? report.overall.reviews,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {i18next.t(isSandboxReport ? 'meeting-package-sandbox-review-queue-info' : 'meeting-package-simulated-review-queue-info')}
            </Typography>
            <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {!isSandboxReport && <TableCell>{i18next.t('meeting-package-scenario')}</TableCell>}
                    <TableCell>{i18next.t(isSandboxReport ? 'meeting-package-sandbox-venue' : 'meeting-package-mock-venue')}</TableCell>
                    {!isSandboxReport && <TableCell>{i18next.t('meeting-package-expectation')}</TableCell>}
                    <TableCell>{i18next.t('meeting-package-best-candidate')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-score')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-evidence')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-conflicts')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report.reviewQueue?.items || []).map(item => (
                    <TableRow key={item.id} hover>
                      {!isSandboxReport && <TableCell>{item.scenario}</TableCell>}
                      <TableCell>
                        {item.venue.name}
                        <Typography variant="caption" display="block">
                          {item.venue.businessAddress1 || '–'}, {item.venue.businessZip || '–'} {item.venue.businessCity || ''}
                        </Typography>
                      </TableCell>
                      {!isSandboxReport && <TableCell>{importValueLabel(item.expectation)}</TableCell>}
                      <TableCell>{item.decision.candidates[0]?.hotelName || '–'}</TableCell>
                      <TableCell>{percent(item.decision.score)}</TableCell>
                      <TableCell>{percent(item.decision.evidenceCoverage)}</TableCell>
                      <TableCell>{item.decision.conflicts.join(', ') || '–'}</TableCell>
                      <TableCell>
                        <Button onClick={() => setSelectedReviewItem(item)}>{i18next.t('meeting-package-details')}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            {!report.reviewQueue && <Alert severity="info">{i18next.t('meeting-package-old-report-no-review-queue')}</Alert>}
          </Box>

          {!isSandboxReport && (
            <Box sx={{ display: reportTab === 3 ? 'block' : 'none' }}>
              <Typography variant="h6">{i18next.t('meeting-package-prioritized-deviations')}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18next.t('meeting-package-scenario')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-venue')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-expectation')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-decision')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-candidate')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-score-runner-up')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-conflicts')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportFailures.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{item.scenario}</TableCell>
                      <TableCell>{item.venueName}</TableCell>
                      <TableCell>{importValueLabel(item.expectation)}</TableCell>
                      <TableCell>{importValueLabel(item.decision)}</TableCell>
                      <TableCell>
                        {item.candidateHotelName || '–'} {item.candidateHotelId ? `#${item.candidateHotelId}` : ''}
                      </TableCell>
                      <TableCell>
                        {percent(item.score)} / {percent(item.runnerUpScore)}
                      </TableCell>
                      <TableCell>{item.conflicts.join(', ') || '–'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {isSandboxReport && (
            <Box sx={{ display: reportTab === 2 ? 'block' : 'none' }}>
              <Typography variant="h6">{i18next.t('meeting-package-api-errors')}</Typography>
              {(report.apiFailures || []).length === 0 ? (
                <Alert severity="success">{i18next.t('meeting-package-all-venue-details-loaded')}</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18next.t('meeting-package-venue-id')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-name')}</TableCell>
                      <TableCell>{i18next.t('meeting-package-error')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report.apiFailures || []).map(item => (
                      <TableRow key={item.externalVenueId}>
                        <TableCell>{item.externalVenueId}</TableCell>
                        <TableCell>{item.venueName || '–'}</TableCell>
                        <TableCell>{item.error}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          )}
        </Stack>
      )}
      {selectedReviewItem && <DryRunReviewDialog item={selectedReviewItem} onClose={() => setSelectedReviewItem(undefined)} />}
    </Box>
  );
};

type ImportErrorEvent = {
  id: number;
  createdAt: string;
  error?: string | null;
  detailsJson?: string | null;
  venue?: { externalVenueId: string; venueName: string } | null;
};

function ImportErrorsDialog({ run, onClose, onOpenVenue }: { run: ImportRunSummary; onClose: () => void; onOpenVenue: (id: string) => void }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const errors = useQuery(MEETING_PACKAGE_IMPORT_ERRORS_QUERY, {
    variables: { runId: run.id, skip: page * rowsPerPage, take: rowsPerPage },
    pollInterval: run.status === 'RUNNING' ? 5000 : 0,
    fetchPolicy: 'cache-and-network',
  });
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18next.t('meeting-package-import-errors-title', { runId: run.id })}</DialogTitle>
      <DialogContent>
        {run.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {run.error}
          </Alert>
        )}
        {errors.error && <Alert severity="error">{errors.error.message}</Alert>}
        {errors.loading && <LinearProgress />}
        {errors.data?.meetingPackageImportErrors.totalCount === 0 && <Alert severity="info">{i18next.t('meeting-package-no-venue-errors')}</Alert>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18next.t('meeting-package-time')}</TableCell>
              <TableCell>{i18next.t('meeting-package-venue-id')}</TableCell>
              <TableCell>{i18next.t('meeting-package-venue')}</TableCell>
              <TableCell>{i18next.t('meeting-package-error')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(errors.data?.meetingPackageImportErrors.errors || []).map((event: ImportErrorEvent) => {
              const details = parseJson<{ externalVenueId?: string; venueName?: string }>(event.detailsJson);
              const externalVenueId = details?.externalVenueId || event.venue?.externalVenueId;
              return (
                <TableRow key={event.id}>
                  <TableCell>{new Date(event.createdAt).toLocaleString(i18next.language)}</TableCell>
                  <TableCell>{externalVenueId || '–'}</TableCell>
                  <TableCell>{details?.venueName || event.venue?.venueName || '–'}</TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{event.error || '–'}</TableCell>
                  <TableCell>
                    {externalVenueId && <Button onClick={() => onOpenVenue(externalVenueId)}>{i18next.t('meeting-package-open')}</Button>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={errors.data?.meetingPackageImportErrors.totalCount ?? 0}
          page={page}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={event => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage={i18next.t('meeting-package-rows-per-page')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18next.t('meeting-package-close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function MeetingPackageImportContent() {
  const user = userSelector();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [matchOutcome, setMatchOutcome] = useState('');
  const [manualRunSelection, setManualRunSelection] = useState<number | null>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState<string>();
  const [selectedErrorRunId, setSelectedErrorRunId] = useState<number>();
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [allImportCountries, setAllImportCountries] = useState(true);
  const [importCountries, setImportCountries] = useState<string[]>([]);
  const countryOptions = useMemo(() => [...allCountries].sort((a, b) => a.label.localeCompare(b.label)), []);
  const runs = useQuery(MEETING_PACKAGE_RUNS_QUERY, { variables: { skip: 0, take: 100 }, pollInterval: 5000 });
  const importRuns = (runs.data?.meetingPackageImportRuns.runs || []) as ImportRunSummary[];
  const activeRun = importRuns.find(run => run.status === 'RUNNING' || run.status === 'QUEUED');
  const selectedErrorRun = importRuns.find(run => run.id === selectedErrorRunId);
  const queuePollInterval = activeRun ? 5000 : 30000;
  const selectedRunId = manualRunSelection === undefined ? (runs.data?.meetingPackageImportRuns.runs[0]?.id ?? null) : manualRunSelection;
  const waitingForRuns = manualRunSelection === undefined && !runs.data && !runs.error;
  useEffect(() => setPage(0), [selectedRunId]);
  const queue = useQuery(MEETING_PACKAGE_QUEUE_QUERY, {
    variables: {
      includeAll: true,
      includeInactive,
      runId: selectedRunId,
      matchOutcome: matchOutcome || null,
      search: search || null,
      skip: page * rowsPerPage,
      take: rowsPerPage,
    },
    pollInterval: queuePollInterval,
    skip: waitingForRuns,
  });
  const queueCounts = useQuery(MEETING_PACKAGE_QUEUE_COUNTS_QUERY, {
    variables: { search: search || null, runId: selectedRunId, includeInactive },
    pollInterval: queuePollInterval,
    skip: waitingForRuns,
  });
  const inactiveQueueCount = useQuery(MEETING_PACKAGE_INACTIVE_COUNT_QUERY, {
    variables: { search: search || null, runId: selectedRunId, matchOutcome: matchOutcome || null },
    pollInterval: queuePollInterval,
    fetchPolicy: 'cache-and-network',
    skip: waitingForRuns,
  });
  const inactiveCount = inactiveQueueCount.data?.meetingPackageQueue.totalCount;
  useEffect(() => {
    if (inactiveCount === 0 && includeInactive) setIncludeInactive(false);
  }, [inactiveCount, includeInactive]);
  useEffect(() => {
    const totalCount = queue.data?.meetingPackageQueue.totalCount;
    if (totalCount != null && page > 0 && page * rowsPerPage >= totalCount) {
      setPage(Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1));
    }
  }, [page, queue.data?.meetingPackageQueue.totalCount, rowsPerPage]);
  const dryRuns = useQuery(MEETING_PACKAGE_DRY_RUNS_QUERY, {
    variables: { skip: 0, take: 20 },
    pollInterval: 30000,
    skip: !SHOW_MATCHING_DRY_RUN,
  });
  const [runDiscovery, discovery] = useMutation(RUN_MEETING_PACKAGE_DISCOVERY, {
    refetchQueries: [
      MEETING_PACKAGE_QUEUE_QUERY,
      MEETING_PACKAGE_QUEUE_COUNTS_QUERY,
      MEETING_PACKAGE_INACTIVE_COUNT_QUERY,
      MEETING_PACKAGE_RUNS_QUERY,
    ],
  });
  const [cancelDiscovery, cancellation] = useMutation(CANCEL_MEETING_PACKAGE_DISCOVERY, {
    refetchQueries: [MEETING_PACKAGE_RUNS_QUERY],
  });
  const discoveryDisabled = discovery.loading || !!activeRun || runs.loading || !!runs.error;

  const startDiscovery = async () => {
    try {
      const response = await runDiscovery({ variables: { countries: allImportCountries ? [] : importCountries } });
      if (response.data?.runMeetingPackageDiscovery.id != null) setManualRunSelection(response.data.runMeetingPackageDiscovery.id);
      setStartDialogOpen(false);
    } catch {
      // Apollo exposes the error through the mutation result rendered below.
    }
  };

  const stopDiscovery = async (runId: number) => {
    try {
      await cancelDiscovery({ variables: { runId } });
    } catch {
      // Apollo exposes the error through the mutation result rendered below.
    }
  };

  if (!user?.isSeminargo) return <Alert severity="error">{i18next.t('meeting-package-no-permission')}</Alert>;

  return (
    <>
      <Dialog open={startDialogOpen} onClose={() => !discovery.loading && setStartDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{i18next.t('meeting-package-start-venue-sync')}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={<Checkbox checked={allImportCountries} onChange={(_, checked) => setAllImportCountries(checked)} disabled={discovery.loading} />}
            label={i18next.t('meeting-package-all-countries')}
          />
          <Autocomplete
            multiple
            options={countryOptions}
            value={countryOptions.filter(country => importCountries.includes(country.code))}
            onChange={(_, selected) => setImportCountries(selected.map(country => country.code))}
            getOptionLabel={country => country.label}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            disabled={allImportCountries || discovery.loading}
            renderInput={params => <TextField {...params} label={i18next.t('meeting-package-import-countries')} />}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {i18next.t('meeting-package-import-countries-help')}
          </Typography>
          {discovery.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {discovery.error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={discovery.loading} onClick={() => setStartDialogOpen(false)}>
            {i18next.t('meeting-package-cancel')}
          </Button>
          <Button variant="contained" disabled={discoveryDisabled || (!allImportCountries && importCountries.length === 0)} onClick={startDiscovery}>
            {i18next.t('meeting-package-start-venue-sync')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
          <Typography variant="h4">{i18next.t('meeting-package-import-title')}</Typography>
          <Button
            variant="contained"
            startIcon={discovery.loading ? <CircularProgress size={18} /> : <PlayArrowIcon />}
            disabled={discoveryDisabled}
            onClick={() => setStartDialogOpen(true)}
          >
            {i18next.t('meeting-package-start-venue-sync')}
          </Button>
        </Stack>
        {activeRun && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={1}>
              <Typography variant="body2">
                {activeRun.status === 'QUEUED'
                  ? i18next.t('meeting-package-import-queued')
                  : activeRun.totalVenueCount == null
                    ? i18next.t('meeting-package-import-progress-loading')
                    : i18next.t('meeting-package-import-progress', {
                        processed: activeRun.processedVenueCount,
                        total: activeRun.totalVenueCount,
                        percent: importRunPercent(activeRun),
                      })}
              </Typography>
              <Button
                color="warning"
                size="small"
                disabled={cancellation.loading || !!activeRun.cancelRequestedAt}
                onClick={() => stopDiscovery(activeRun.id)}
              >
                {i18next.t(activeRun.cancelRequestedAt ? 'meeting-package-import-cancellation-requested' : 'meeting-package-cancel-import')}
              </Button>
            </Stack>
            <LinearProgress variant={activeRun.totalVenueCount == null ? 'indeterminate' : 'determinate'} value={importRunPercent(activeRun) ?? 0} />
          </Box>
        )}
        {runs.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {runs.error.message}
          </Alert>
        )}
        {discovery.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {discovery.error.message}
          </Alert>
        )}
        {cancellation.error && <Alert severity="error">{cancellation.error.message}</Alert>}
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label={i18next.t('meeting-package-review-queue')} />
          <Tab label={i18next.t('meeting-package-import-runs')} />
          {SHOW_MATCHING_DRY_RUN && <Tab label={i18next.t('meeting-package-matching-dry-run')} />}
        </Tabs>
        {tab === 0 && (
          <Box mt={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
              <TextField
                size="small"
                label={i18next.t('meeting-package-search')}
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
              <TextField
                select
                size="small"
                label={i18next.t('meeting-package-import-run-filter')}
                helperText={selectedRunId ? i18next.t('meeting-package-import-run-current-status') : undefined}
                value={selectedRunId ?? ''}
                onChange={event => {
                  setManualRunSelection(event.target.value === '' ? null : Number(event.target.value));
                  setPage(0);
                }}
                sx={{ minWidth: 230 }}
              >
                <MenuItem value="">{i18next.t('meeting-package-all-import-runs')}</MenuItem>
                {(runs.data?.meetingPackageImportRuns.runs || []).map((run: ImportRunSummary) => (
                  <MenuItem key={run.id} value={run.id}>
                    #{run.id} · {new Date(run.startedAt).toLocaleString(i18next.language)} · {importValueLabel(run.status)} ·{' '}
                    {run.countries?.length ? run.countries.join(', ') : i18next.t('meeting-package-all-countries')}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                startIcon={<RefreshIcon />}
                onClick={() => {
                  queue.refetch();
                  queueCounts.refetch();
                  inactiveQueueCount.refetch();
                }}
              >
                {i18next.t('meeting-package-refresh')}
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
              {(
                [
                  ['', 'meeting-package-all', 'total'],
                  ['MATCHED', 'meeting-package-match-outcome-matched', 'matched'],
                  ['NEW_HOTEL', 'meeting-package-match-outcome-new-hotel', 'newHotel'],
                  ['NEEDS_REVIEW', 'meeting-package-match-outcome-needs-review', 'needsReview'],
                  ['INCOMPLETE', 'meeting-package-import-value-incomplete', 'incomplete'],
                ] as const
              ).map(([value, label, countKey]) => (
                <Chip
                  key={value}
                  label={`${i18next.t(label)} (${queueCounts.data?.meetingPackageQueueCounts[countKey] ?? '–'})`}
                  color={matchOutcome === value ? 'primary' : 'default'}
                  variant={matchOutcome === value ? 'filled' : 'outlined'}
                  onClick={() => {
                    setMatchOutcome(value);
                    setPage(0);
                  }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactive}
                    disabled={inactiveCount == null || inactiveCount === 0}
                    onChange={(_event, checked) => {
                      setIncludeInactive(checked);
                      setPage(0);
                    }}
                  />
                }
                label={`${i18next.t('meeting-package-show-inactive')} (${inactiveCount ?? '–'})`}
              />
            </Stack>
            {queue.loading && <CircularProgress />}
            {queue.error && <Alert severity="error">{queue.error.message}</Alert>}
            {queueCounts.error && <Alert severity="error">{queueCounts.error.message}</Alert>}
            {inactiveQueueCount.error && <Alert severity="error">{inactiveQueueCount.error.message}</Alert>}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18next.t('meeting-package-venue')}</TableCell>
                  <TableCell>{i18next.t('meeting-package-status')}</TableCell>
                  <TableCell>{i18next.t('meeting-package-decision')}</TableCell>
                  <TableCell>{i18next.t('meeting-package-score')}</TableCell>
                  <TableCell>{i18next.t('meeting-package-evidence')}</TableCell>
                  <TableCell>{i18next.t('meeting-package-last-seen')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(queue.data?.meetingPackageQueue.venues || []).map((venue: QueueVenue) => (
                  <TableRow key={venue.id} hover>
                    <TableCell>
                      {venue.venueName}
                      <Typography variant="caption" display="block">
                        ID {venue.externalVenueId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          venue.status === 'INACTIVE' &&
                          queueCounts.data?.meetingPackageQueueCounts.lastSuccessfulRunStartedAt &&
                          new Date(venue.lastSeenAt) < new Date(queueCounts.data.meetingPackageQueueCounts.lastSuccessfulRunStartedAt)
                            ? i18next.t('meeting-package-not-in-source')
                            : importValueLabel(['AUTO_CREATE', 'AUTO_MATCH'].includes(venue.matchMethod || '') ? venue.matchMethod : venue.status)
                        }
                        color={statusColor(venue.status)}
                      />
                      {queueCounts.data?.meetingPackageQueueCounts.lastRunStartedAt &&
                        new Date(venue.lastSeenAt) < new Date(queueCounts.data.meetingPackageQueueCounts.lastRunStartedAt) && (
                          <MuiTooltip title={i18next.t('meeting-package-older-entry-tooltip')}>
                            <Chip size="small" label={i18next.t('meeting-package-older-entry')} variant="outlined" sx={{ ml: 0.5 }} />
                          </MuiTooltip>
                        )}
                      {automaticResultLabel(venue) && <Chip size="small" label={automaticResultLabel(venue)} variant="outlined" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      {venue.matchMethod === 'AUTO_CREATE' || venue.matchMethod === 'AUTO_MATCH'
                        ? '–'
                        : importValueLabel(venue.matchMethod || venue.resolution)}
                    </TableCell>
                    <TableCell>{percent(venue.matchScore)}</TableCell>
                    <TableCell>{percent(venue.evidenceCoverage)}</TableCell>
                    <TableCell>{new Date(venue.lastSeenAt).toLocaleString(i18next.language)}</TableCell>
                    <TableCell>
                      <Button onClick={() => setSelected(venue.externalVenueId)}>{i18next.t('meeting-package-open')}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={queue.data?.meetingPackageQueue.totalCount ?? 0}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={event => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage={i18next.t('meeting-package-rows-per-page')}
            />
          </Box>
        )}
        {tab === 1 && (
          <Box mt={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {i18next.t('meeting-package-run-accounting-help')}
            </Alert>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1900 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell colSpan={3}>{i18next.t('meeting-package-import-runs')}</TableCell>
                    <TableCell colSpan={5}>{i18next.t('meeting-package-run-selection-group')}</TableCell>
                    <TableCell colSpan={7}>{i18next.t('meeting-package-run-outcomes-group')}</TableCell>
                    <TableCell colSpan={2}>{i18next.t('meeting-package-run-other-group')}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell>{i18next.t('meeting-package-start')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-status')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-import-countries')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-api')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-retries')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-prefiltered')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-import-progress-heading')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-excluded')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-discovered')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-updated')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-linked')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-created')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-review')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-ignored')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-discovered-failed')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-inactive')}</TableCell>
                    <TableCell>{i18next.t('meeting-package-run-all-errors')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(runs.data?.meetingPackageImportRuns.runs || []).map((run: ImportRunSummary) => (
                    <TableRow key={run.id}>
                      <TableCell>{new Date(run.startedAt).toLocaleString(i18next.language)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={
                            run.cancelRequestedAt && run.status === 'RUNNING'
                              ? i18next.t('meeting-package-import-cancellation-requested')
                              : importValueLabel(run.status)
                          }
                          color={statusColor(run.status === 'SUCCESS' ? 'READY' : run.status === 'FAILED' ? 'FAILED' : run.status)}
                        />
                        {run.accountingReconstructed && (
                          <Typography variant="caption" display="block">
                            {i18next.t('meeting-package-run-historical')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {run.countries?.length
                          ? run.countries.map(code => allCountriesByCode[code]?.name || code).join(', ')
                          : i18next.t('meeting-package-all-countries')}
                      </TableCell>
                      <TableCell>{run.apiVenueCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>{run.retryVenueCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>{run.prefilteredVenueCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {run.totalVenueCount == null ? (
                          '–'
                        ) : (
                          <>
                            {i18next.t('meeting-package-import-progress', {
                              processed: run.processedVenueCount,
                              total: run.totalVenueCount,
                              percent: importRunPercent(run),
                            })}
                            <LinearProgress variant="determinate" value={importRunPercent(run) ?? 0} sx={{ mt: 0.5 }} />
                          </>
                        )}
                      </TableCell>
                      <TableCell>{run.excludedVenueCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>{run.discoveredCount}</TableCell>
                      <TableCell>{run.updatedCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>
                        {run.linkedCount}
                        {run.autoMatchEnabled === false && (
                          <MuiTooltip
                            title={i18next.t('meeting-package-suggested-linked-tooltip', {
                              count: run.suggestedLinkedCount,
                              actual: run.linkedCount,
                            })}
                          >
                            <WarningAmberIcon color="warning" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                          </MuiTooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {run.createdCount}
                        {run.autoCreateEnabled === false && (
                          <MuiTooltip
                            title={i18next.t('meeting-package-suggested-created-tooltip', {
                              count: run.suggestedCreatedCount,
                              actual: run.createdCount,
                            })}
                          >
                            <WarningAmberIcon color="warning" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                          </MuiTooltip>
                        )}
                      </TableCell>
                      <TableCell>{run.reviewCount}</TableCell>
                      <TableCell>{run.ignoredCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>{run.discoveredFailedCount ?? i18next.t('meeting-package-run-not-recorded')}</TableCell>
                      <TableCell>{run.inactiveCount}</TableCell>
                      <TableCell>
                        {run.failedCount > 0 || run.error || run.status === 'FAILED' ? (
                          <Button color="error" size="small" onClick={() => setSelectedErrorRunId(run.id)} startIcon={<WarningAmberIcon />}>
                            {run.failedCount > 0 && `${run.failedCount} · `}
                            {i18next.t('meeting-package-error-details')}
                          </Button>
                        ) : (
                          run.failedCount
                        )}
                      </TableCell>
                      <TableCell>
                        {(run.status === 'RUNNING' || run.status === 'QUEUED') && (
                          <Button
                            size="small"
                            color="warning"
                            disabled={cancellation.loading || !!run.cancelRequestedAt}
                            onClick={() => stopDiscovery(run.id)}
                          >
                            {i18next.t(run.cancelRequestedAt ? 'meeting-package-import-cancellation-requested' : 'meeting-package-cancel-import')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
        {SHOW_MATCHING_DRY_RUN && tab === 2 && <MatchingDryRunPanel query={dryRuns} />}
      </Box>
      {selectedErrorRun && (
        <ImportErrorsDialog
          run={selectedErrorRun}
          onClose={() => setSelectedErrorRunId(undefined)}
          onOpenVenue={id => {
            setSelectedErrorRunId(undefined);
            setSelected(id);
          }}
        />
      )}
      {selected && <VenueReviewDialog externalVenueId={selected} onClose={() => setSelected(undefined)} />}
    </>
  );
}
